import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Camera, Image as ImageIcon, Upload, X, Check } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoUpload = ({ 
  onPhotoUploaded, 
  currentPhotoUrl = null, 
  onPhotoRemoved = null,
  maxPhotos = 1,
  label = "Photo",
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState(() => {
    if (!currentPhotoUrl) return [];
    if (Array.isArray(currentPhotoUrl)) {
      return currentPhotoUrl.filter(url => url && typeof url === 'string' && url.trim() !== '');
    }
    if (currentPhotoUrl && typeof currentPhotoUrl === 'string' && currentPhotoUrl.trim() !== '') {
      return [currentPhotoUrl];
    }
    return [];
  });
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Update photos when currentPhotoUrl changes
  useEffect(() => {
    if (!currentPhotoUrl) {
      setPhotos([]);
    } else if (Array.isArray(currentPhotoUrl)) {
      // Filter out empty, null, or invalid URLs
      const validPhotos = currentPhotoUrl.filter(url => url && typeof url === 'string' && url.trim() !== '');
      setPhotos(validPhotos);
    } else if (currentPhotoUrl && typeof currentPhotoUrl === 'string' && currentPhotoUrl.trim() !== '') {
      setPhotos([currentPhotoUrl]);
    } else {
      setPhotos([]);
    }
  }, [currentPhotoUrl]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setError('');
    setUploading(true);

    try {
      // For single photo (profile picture), replace existing. For multiple photos, add up to limit.
      let filesToUpload;
      if (maxPhotos === 1) {
        // Profile picture: always upload 1 file (replace existing)
        filesToUpload = files.slice(0, 1);
      } else {
        // Portfolio: upload up to remaining limit
        filesToUpload = files.slice(0, maxPhotos - photos.length);
      }
      
      const uploadPromises = filesToUpload.map(uploadFile);
      const uploadedUrls = await Promise.all(uploadPromises);
      
      let newPhotos;
      if (maxPhotos === 1) {
        // Profile picture: replace entirely
        newPhotos = uploadedUrls;
      } else {
        // Portfolio: add to existing, up to limit
        newPhotos = [...photos, ...uploadedUrls].slice(0, maxPhotos);
      }
      setPhotos(newPhotos);
      
      if (onPhotoUploaded) {
        onPhotoUploaded(maxPhotos === 1 ? newPhotos[0] : newPhotos);
      }
    } catch (error) {
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await axios.post(`${API_URL}/api/upload/photo`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Return base64 data URL for persistent storage instead of file URL
    return response.data.base64 || response.data.url;
  };

  const removePhoto = async (index) => {
    try {
      const photoToRemove = photos[index];
      
      // Extract filename from URL
      const filename = photoToRemove.split('/').pop();
      
      // Delete from server
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_URL}/api/upload/photo/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const newPhotos = photos.filter((_, i) => i !== index);
      // Additional filtering to ensure only valid URLs remain
      const cleanPhotos = newPhotos.filter(url => url && typeof url === 'string' && url.trim() !== '');
      setPhotos(cleanPhotos);
      
      // Reset file inputs to allow re-upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
      
      // Clear any error state
      setError('');
      
      if (onPhotoUploaded) {
        onPhotoUploaded(maxPhotos === 1 ? (cleanPhotos[0] || null) : cleanPhotos);
      }
      
      if (onPhotoRemoved) {
        onPhotoRemoved(photoToRemove);
      }
    } catch (error) {
      console.error('Failed to remove photo:', error);
      setError('Failed to remove photo');
    }
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  // Count only valid photos for upload limit
  const validPhotos = photos.filter(url => url && typeof url === 'string' && url.trim() !== '');
  // For single photo uploads (profile pictures), always allow uploading/replacing
  // For multi-photo uploads (portfolios), check the limit
  const canUploadMore = maxPhotos === 1 ? true : validPhotos.length < maxPhotos;

  return (
    <div className={className}>
      <div className="space-y-3">
        <label className="block text-sm font-medium" style={{color: 'var(--brand-navy)'}}>
          {label} {maxPhotos > 1 && `(${validPhotos.length}/${maxPhotos})`}
        </label>
        
        {error && (
          <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
            {error}
          </div>
        )}

        {/* Photo Preview Grid */}
        {photos.length > 0 && (
          <div className={`grid gap-3 ${maxPhotos > 1 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
            {photos.map((photo, index) => (
              <Card key={index} className="relative">
                <CardContent className="p-0">
                  <div className="relative aspect-square">
                    <img
                      src={photo.startsWith('data:') ? photo : `${API_URL}${photo}`}
                      alt={`Uploaded ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        console.log(`❌ Image failed to load: ${e.target.src.substring(0, 100)}...`);
                        // Hide the entire card if image fails to load
                        e.target.parentElement.parentElement.parentElement.style.display = 'none';
                      }}
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 w-6 h-6 p-0"
                      onClick={() => removePhoto(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Buttons */}
        {canUploadMore && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={openCamera}
              disabled={uploading}
              className="flex-1"
            >
              <Camera className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : (maxPhotos === 1 && validPhotos.length > 0) ? 'Change Photo' : 'Take Photo'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={openGallery}
              disabled={uploading}
              className="flex-1"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : (maxPhotos === 1 && validPhotos.length > 0) ? 'Change Photo' : 'Choose Photo'}
            </Button>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-600 animate-pulse" />
              <span className="text-blue-600 text-sm">Uploading photo...</span>
            </div>
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={maxPhotos > 1}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Help text */}
        <p className="text-xs text-gray-500">
          Supports JPG, PNG, HEIC. Max 10MB per photo. 
          {maxPhotos > 1 ? ` Upload up to ${maxPhotos} photos.` : ''}
        </p>
      </div>
    </div>
  );
};

export default PhotoUpload;